//
//  knock.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 13, 2026
//  Author: Bob Keathley
//
//  Knock in-app feed plumbing (SIG-72, spec 6j). The feed token lives only in
//  this module's closure and React state, never in session or local storage.
//
import { useEffect, useState } from 'react'
import { api } from '@shared/api/client'
import { isOk, type Envelope } from '@shared/api/envelope'
import type { NotificationToken } from '@shared/api/types'
import type { Session } from '@shared/auth'

// Knock in-app feed plumbing (SIG-72, spec §6j). The token lives in this module's closure and in
// React state only -- never sessionStorage/localStorage, matching the bearer-token rule in auth.ts.

export const KNOCK_PUBLIC_KEY: string | undefined = import.meta.env.VITE_KNOCK_PUBLIC_KEY || undefined
export const KNOCK_FEED_CHANNEL_ID: string | undefined = import.meta.env.VITE_KNOCK_FEED_CHANNEL_ID || undefined

/** Refetch this many seconds before `expires_at`. */
export const REFRESH_LEAD_S = 120

/** The feed mounts only with a public key and outside an assumed session (an operator must not
 * read or mark a tenant user's notifications). */
export function feedAllowed(session: Pick<Session, 'user'>, publicKey: string | undefined = KNOCK_PUBLIC_KEY): boolean {
  return Boolean(publicKey) && !session.user.acting_tenant_id
}

export type TokenState =
  | { kind: 'loading' }
  | { kind: 'ready'; userId: string; token: string; expiresAt: number }
  | { kind: 'hidden' }   // 503 notifications_disabled, or any failure: no bell, no toast

type Fetcher = (path: string, init: RequestInit, token: string) => Promise<Envelope<NotificationToken>>

export type Timers = {
  set: (fn: () => void, ms: number) => unknown
  clear: (h: unknown) => void
  now: () => number   // ms
}
const realTimers: Timers = { set: (f, ms) => setTimeout(f, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>), now: () => Date.now() }

/**
 * Fetches the token and keeps it fresh. Returns a stop function. Does nothing -- and never calls
 * the token endpoint -- when the feed is not allowed for this session.
 */
export function runTokenFeed(
  session: Pick<Session, 'token' | 'user'>,
  onState: (s: TokenState) => void,
  opts: { publicKey?: string; fetcher?: Fetcher; timers?: Timers } = {},
): () => void {
  const publicKey = 'publicKey' in opts ? opts.publicKey : KNOCK_PUBLIC_KEY
  if (!feedAllowed(session, publicKey)) {
    onState({ kind: 'hidden' })
    return () => {}
  }
  const fetcher = opts.fetcher ?? ((p, i, t) => api<NotificationToken>(p, i, t))
  const timers = opts.timers ?? realTimers
  let stopped = false
  let handle: unknown

  const load = async () => {
    const r = await fetcher('/notifications/token', {}, session.token)
    if (stopped) return
    if (!isOk(r)) {
      onState({ kind: 'hidden' })   // 503 notifications_disabled and everything else: hide quietly
      return
    }
    onState({ kind: 'ready', userId: r.data.user_id, token: r.data.token, expiresAt: r.data.expires_at })
    const wait = Math.max(5_000, (r.data.expires_at - REFRESH_LEAD_S) * 1000 - timers.now())
    handle = timers.set(load, wait)
  }
  onState({ kind: 'loading' })
  void load()
  return () => {
    stopped = true
    if (handle !== undefined) timers.clear(handle)
  }
}

export function useKnockToken(session: Session): TokenState {
  const [state, setState] = useState<TokenState>({ kind: 'loading' })
  useEffect(() => runTokenFeed(session, setState), [session.token, session.user.id, session.user.acting_tenant_id])
  return state
}
